// Per-route SEO. No external dependency: sets document title + meta imperatively
// so the puppeteer prerender captures unique tags per route (no route inherits
// the homepage title). Brand voice: calm, second person, no exclamation points,
// no dashes, no shame or medical claims.
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { faqPageJsonLd, PRODUCT_JSONLD, PRODUCT_IMAGE, MERCHANT_OFFER } from "./seoPages";

const ORIGIN = "https://commandcenteradhd.com";

const META = {
  "/": {
    title: "CommandCenterADHD | When your brain feels overwhelmed, start here",
    description:
      "The ADHD planning system that helps you choose one priority, find your next step, and restart without guilt. $17 one time, no subscription.",
  },
  "/get-started": {
    title: "Get CommandCenterADHD for $17, one time",
    description:
      "Get the CommandCenterADHD Notion system for $17, one time. No subscription. A calm ADHD planning setup with a 30 day money back guarantee.",
  },
  "/free-planner": {
    title: "Free ADHD daily planner PDF | CommandCenterADHD",
    description:
      "Download the free CommandCenterADHD daily planner PDF, built for the way ADHD planning actually goes. One priority, quick wins, and a gentle reset.",
  },
  "/guides": {
    title: "Setup Video Guides for the ADHD Command Center",
    description:
      "Short setup video guides that show you how to build and use the CommandCenterADHD Notion system at your own pace, one calm step at a time.",
  },
  "/about": {
    title: "About CommandCenterADHD | Why we built an ADHD planner",
    description:
      "Meet the founder of CommandCenterADHD and the calm, no shame approach to ADHD daily planning behind the $17 one time Notion system.",
  },
  "/community": {
    title: "The CommandCenterADHD Community | Quiet ADHD Support",
    description:
      "A quiet CommandCenterADHD community with no likes and no streaks, where members share how they plan, restart, and stay on track with ADHD.",
  },
  "/license": {
    title: "License for the CommandCenterADHD Notion System",
    description:
      "The personal use license that comes with the CommandCenterADHD Notion planning system. What you can and cannot do with your copy.",
  },
  "/privacy": {
    title: "Privacy Policy | CommandCenterADHD",
    description:
      "How CommandCenterADHD collects, uses, and protects your information across the website, the Notion system, and the app.",
  },
  "/terms": {
    title: "Terms and Conditions | CommandCenterADHD",
    description:
      "The terms that apply when you buy and use CommandCenterADHD, the Notion planning system, and the Command Center app.",
  },
  "/refund-policy": {
    title: "Refund Policy and 30 Day Guarantee | CommandCenterADHD",
    description:
      "Try CommandCenterADHD for 30 days. If it does not fit your routine, email us for a full refund. No forms and no questions.",
  },
  "/cookie-policy": {
    title: "Cookie Policy | CommandCenterADHD",
    description:
      "How CommandCenterADHD uses cookies for the site and analytics, and how you control them from the cookie settings at any time.",
  },
  "/accessibility": {
    title: "Accessibility Statement | CommandCenterADHD",
    description:
      "Our commitment to keeping CommandCenterADHD usable, calm, and low friction for everyone, including people who use assistive technology.",
  },
  "/recover-access": {
    title: "Recover access | CommandCenterADHD",
    description:
      "Lost your access link. Recover your CommandCenterADHD access with the email you used at checkout.",
  },
  "/adhd-daily-planner": {
    title: "ADHD Daily Planner for Real Days | CommandCenterADHD",
    description:
      "A calm ADHD daily planner that helps you pick one priority, focus, and restart when the day slips. Free printable, plus the $17 Command Center.",
  },
  "/adhd-overwhelm": {
    title: "When ADHD Overwhelm Hits, Start Here | CommandCenterADHD",
    description:
      "When ADHD overwhelm hits, shrink the day to one thing and get moving with Low Battery Mode in the $17 Command Center. No subscription.",
  },
  "/adhd-planner-no-subscription": {
    title: "ADHD Planner With No Subscription, $17 One Time",
    description:
      "An ADHD planner you buy once for $17. No subscription, no recurring fees, future updates included. Runs on the free Notion plan.",
  },
  "/what-should-i-do-next": {
    title: "What Should I Do Next? Free ADHD Tool",
    description:
      "A free tool for ADHD overwhelm. Dump your tasks, pick your energy and time, and get one clear next step to start. Nothing you type is saved.",
  },
};

// Prefixes that must never be indexed.
const NOINDEX_PREFIXES = ["/access", "/thank-you", "/admin", "/today", "/settings"];

const OG_IMAGE = `${ORIGIN}/cc-og-image.jpg`;

// Per-route JSON-LD. Organization + WebSite stay global in index.html. Product
// and FAQPage are injected here so they never leak onto the wrong route:
// Product only on home and the no subscription page, and each FAQPage matches
// that page's visible FAQ text exactly (shared source in seoPages.js).
const HOME_PRODUCT = {
  "@context": "https://schema.org",
  "@type": "Product",
  name: "Notion Command Center",
  description:
    "An ADHD daily planning system. One priority, quick wins, honest time blocks, a brain dump inbox, energy filtered task views, a weekly reset, a low battery mode, and a private community.",
  image: PRODUCT_IMAGE,
  brand: { "@type": "Brand", name: "CommandCenterADHD" },
  offers: MERCHANT_OFFER,
};

const HOME_FAQ = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    { "@type": "Question", name: "Will this cure my ADHD?", acceptedAnswer: { "@type": "Answer", text: "No. Nothing on this site is medical advice or treatment. It is a planning system designed to reduce the planning friction many people with ADHD describe." } },
    { "@type": "Question", name: "What if I abandon it like everything else?", acceptedAnswer: { "@type": "Answer", text: "The system assumes you will fall off. The reset ritual takes 2 minutes and restarts the whole thing without backfilling a single missed day. And the 30 day guarantee means abandoning it costs you nothing." } },
    { "@type": "Question", name: "Do I need to know Notion?", acceptedAnswer: { "@type": "Answer", text: "No. It works the moment you duplicate it, and the included community has a setup walkthrough if you want company while you start." } },
    { "@type": "Question", name: "What is the difference between the free PDF and the paid system?", acceptedAnswer: { "@type": "Answer", text: "The free PDF is the one page daily planner. The paid Notion Command Center adds a brain dump inbox, energy filtered task views, a weekly reset, a low battery mode, future updates, and the private community." } },
    { "@type": "Question", name: "How do I get my planner after I enter my email?", acceptedAnswer: { "@type": "Answer", text: "Instantly. The planner opens in your browser and a copy lands in your inbox so you always have the link." } },
  ],
};

const JSONLD = {
  "/": [HOME_PRODUCT, HOME_FAQ],
  "/adhd-daily-planner": [faqPageJsonLd("/adhd-daily-planner")],
  "/adhd-overwhelm": [faqPageJsonLd("/adhd-overwhelm")],
  "/adhd-planner-no-subscription": [PRODUCT_JSONLD, faqPageJsonLd("/adhd-planner-no-subscription")],
};

function applyJsonLd(path) {
  if (typeof document === "undefined") return;
  document.head.querySelectorAll('script[data-managed-jsonld="1"]').forEach((el) => el.remove());
  const blocks = JSONLD[path] || [];
  for (const obj of blocks) {
    if (!obj) continue;
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.setAttribute("data-managed-jsonld", "1");
    s.textContent = JSON.stringify(obj);
    document.head.appendChild(s);
  }
}

function setMetaByName(name, content) {
  let el = document.head.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setMetaByProp(prop, content) {
  let el = document.head.querySelector(`meta[property="${prop}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", prop);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setRobots(noindex) {
  let el = document.head.querySelector('meta[name="robots"]');
  if (noindex) {
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute("name", "robots");
      document.head.appendChild(el);
    }
    el.setAttribute("content", "noindex, nofollow");
  } else if (el) {
    el.parentNode.removeChild(el);
  }
}

function applySeo(pathname) {
  if (typeof document === "undefined") return;
  const path = pathname.replace(/\/+$/, "") || "/";
  const meta = META[path];
  const noindex = NOINDEX_PREFIXES.some((p) => path === p || path.startsWith(p + "/"));
  setRobots(noindex);
  applyJsonLd(path);

  // Google Search Console verification (owner supplies the token via env).
  const gsv = process.env.REACT_APP_GOOGLE_SITE_VERIFICATION;
  if (gsv) setMetaByName("google-site-verification", gsv);

  // Only known marketing/legal routes get bespoke tags; app/dynamic routes keep base tags.
  if (!meta) return;

  const canonical = path === "/" ? `${ORIGIN}/` : `${ORIGIN}${path}`;
  document.title = meta.title;
  setMetaByName("description", meta.description);
  setCanonical(canonical);
  setMetaByProp("og:title", meta.title);
  setMetaByProp("og:description", meta.description);
  setMetaByProp("og:url", canonical);
  setMetaByProp("og:image", OG_IMAGE);
  setMetaByName("twitter:title", meta.title);
  setMetaByName("twitter:description", meta.description);
  setMetaByName("twitter:card", "summary_large_image");
  setMetaByName("twitter:image", OG_IMAGE);
}

export default function RouteSeo() {
  const { pathname } = useLocation();
  // Apply synchronously during render so the static prerender snapshot captures the
  // correct per-route head immediately (an effect can be missed by the snapshot).
  applySeo(pathname);
  // Re-apply on client-side navigation.
  useEffect(() => {
    applySeo(pathname);
  }, [pathname]);
  return null;
}
